import { useEffect, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  FileText,
  Link2,
  Users,
  MessageSquare,
  History,
  Trash2,
  X,
} from 'lucide-react';
import { useTaskPanelStore, TabType } from '@/stores/taskPanelStore';
import { useTaskQuery, useDeleteTask } from '@/hooks/useTasks';
import { TaskDetails } from './TaskDetails';
import { TaskDependencies } from './TaskDependencies';
import { TaskAssignments } from './TaskAssignments';
import { TaskComments } from './TaskComments';
import { TaskHistory } from './TaskHistory';

interface TaskPanelProps {
  sheetId: string;
}

const TAB_CONFIG: Array<{
  id: TabType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { id: 'details', label: 'Details', icon: FileText },
  { id: 'dependencies', label: 'Dependencies', icon: Link2 },
  { id: 'assignments', label: 'Assignments', icon: Users },
  { id: 'comments', label: 'Comments', icon: MessageSquare },
  { id: 'history', label: 'History', icon: History },
];

export function TaskPanel({ sheetId }: TaskPanelProps) {
  const { isOpen, taskId, activeTab, closePanel, setActiveTab } =
    useTaskPanelStore();

  const { data: task, isLoading } = useTaskQuery(taskId || '');
  const deleteTaskMutation = useDeleteTask(sheetId);

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closePanel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closePanel]);

  const handleDelete = useCallback(() => {
    if (taskId) {
      deleteTaskMutation.mutate(
        { taskId, deleteSubtasks: false },
        {
          onSuccess: () => {
            closePanel();
          },
        }
      );
    }
  }, [taskId, deleteTaskMutation, closePanel]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        closePanel();
      }
    },
    [closePanel]
  );

  if (!taskId) return null;

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl md:max-w-2xl p-0 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <SheetHeader className="flex-1 pr-4">
            {isLoading ? (
              <div className="h-7 w-48 bg-gray-200 animate-pulse rounded" />
            ) : (
              <SheetTitle className="text-xl font-semibold truncate">
                {task?.name || 'Task Details'}
              </SheetTitle>
            )}
          </SheetHeader>

          <div className="flex items-center gap-2">
            {/* Delete Button */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Task</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete "{task?.name}"? This action
                    cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* Close Button */}
            <Button variant="ghost" size="icon" onClick={closePanel}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as TabType)}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div className="border-b px-6">
            <TabsList className="h-12 w-full justify-start gap-1 bg-transparent p-0">
              {TAB_CONFIG.map(({ id, label, icon: Icon }) => (
                <TabsTrigger
                  key={id}
                  value={id}
                  className="relative h-12 rounded-none border-b-2 border-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground"
                >
                  <Icon className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">{label}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <ScrollArea className="flex-1">
            <div className="p-6">
              <TabsContent value="details" className="mt-0">
                {task && <TaskDetails task={task} sheetId={sheetId} />}
              </TabsContent>

              <TabsContent value="dependencies" className="mt-0">
                {task && <TaskDependencies task={task} sheetId={sheetId} />}
              </TabsContent>

              <TabsContent value="assignments" className="mt-0">
                {task && <TaskAssignments task={task} sheetId={sheetId} />}
              </TabsContent>

              <TabsContent value="comments" className="mt-0">
                {task && <TaskComments task={task} />}
              </TabsContent>

              <TabsContent value="history" className="mt-0">
                {task && <TaskHistory task={task} />}
              </TabsContent>
            </div>
          </ScrollArea>
        </Tabs>

        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default TaskPanel;
